import React, { useState } from "react";

import moment from "moment-timezone";

import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import MuiAlert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "../../UI/MainPaper";
import SectionTitle, { ActionButton } from "./SectionTitle";
import { StayRow } from "./StayPanel";

import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

const PAID_COLOR = "#19a86d";
const PARTIAL_COLOR = "#dfad1d";
const UNPAID_COLOR = "#de4141";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  progress: {
    marginTop: theme.spacing(2),
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
  },
  progressBar: {
    borderRadius: 4,
    backgroundColor: "var(--bar-color)",
  },
  balanceLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  groupLabel: {
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
}));

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const toNumber = (value) => Number(value) || 0;

const Money = ({ value, color, bold }) => (
  <Typography
    variant="body2"
    style={{
      color: color || (value < 0 ? UNPAID_COLOR : undefined),
      fontWeight: bold || color ? 600 : undefined,
    }}
  >
    {value < 0 ? "–" : ""}
    {currencyFormat.format(Math.abs(value))}
  </Typography>
);

const breakdownGroups = (type, amounts) =>
  type === "package"
    ? [
        {
          label: "General",
          rows: [
            ["Subtotal", amounts.subtotal],
            ["Tax", amounts.tax],
            ["Resort Fee", amounts.resort_fee_total],
          ],
        },
        {
          label: "Rates",
          rows: [
            ["Rates Subtotal", amounts.rates_subtotal],
            [
              "Rates Subtotal Discount",
              -toNumber(amounts.discount_rates_subtotal),
            ],
            ["Rates Tax", amounts.rates_tax],
            ["Rates Tax Discount", -toNumber(amounts.discount_rates_tax)],
          ],
        },
        {
          label: "Add-ons",
          rows: [
            ["Add-ons Subtotal", amounts.addons_subtotal],
            [
              "Add-ons Subtotal Discount",
              -toNumber(amounts.discount_addons_subtotal),
            ],
            ["Add-ons Tax", amounts.addons_tax],
            ["Add-ons Tax Discount", -toNumber(amounts.discount_addons_tax)],
          ],
        },
      ]
    : [
        {
          label: "Rent",
          rows: [
            ["Total Rent", amounts.totalRent],
            ["Tax", amounts.totalTaxes],
            ["Min Total Due", amounts.minTotalDue],
          ],
        },
        {
          label: "Fees",
          rows: [
            ["Refundable Fees", amounts.totalRefundableFees],
            ["Non Refundable Fees", amounts.totalNonrefundableFees],
          ],
        },
      ];

// Totals shared by the status card and the details card. Package and condo
// orders name their amounts differently.
export const getPaymentSummary = (amounts, type) => {
  const isPackage = type === "package";
  const total = toNumber(isPackage ? amounts.total : amounts.totalDue);
  const discount = toNumber(
    isPackage ? amounts.discount_amount : amounts.totalDiscount
  );
  const totalAfterDiscount = toNumber(
    isPackage
      ? amounts.post_discount_total
      : amounts.postDiscountTotal ?? amounts.totalDue
  );
  const totalPaid = toNumber(amounts.totalPaid);
  const totalRefunds = toNumber(amounts.totalRefunds);
  const netAmountPaid = totalPaid - totalRefunds;
  const amountRemaining = Math.max(0, totalAfterDiscount - netAmountPaid);
  const isPaid = Math.round(amountRemaining * 100) === 0;
  const overpaid = Math.max(0, netAmountPaid - totalAfterDiscount);
  // Ignore sub-cent rounding differences
  const isOverpaid = Math.round(overpaid * 100) > 0;

  return {
    isPackage,
    total,
    discount,
    totalAfterDiscount,
    totalPaid,
    totalRefunds,
    netAmountPaid,
    amountRemaining,
    overpaid,
    isOverpaid,
    isPaid,
    status: isOverpaid
      ? "overpaid"
      : isPaid
      ? "paid"
      : netAmountPaid > 0
      ? "partial"
      : "unpaid",
  };
};

const STATUS_LOOK = {
  paid: { label: "Paid in full", color: PAID_COLOR },
  partial: { label: "Partially paid", color: PARTIAL_COLOR },
  unpaid: { label: "Unpaid", color: UNPAID_COLOR },
  overpaid: { label: "Overpaid", color: UNPAID_COLOR },
};

// At-a-glance: what the order costs, what's been paid, what's still owed
export const PaymentStatus = ({
  amounts,
  type,
  charge_remaining_by,
  onAddCharge,
}) => {
  const classes = useStyles();
  const summary = getPaymentSummary(amounts, type);
  const look = STATUS_LOOK[summary.status];
  const paidPercent =
    summary.totalAfterDiscount > 0
      ? Math.min(
          100,
          (summary.netAmountPaid / summary.totalAfterDiscount) * 100
        )
      : 100;

  const tile = (label, value, color) => (
    <Grid item xs={12} sm={4}>
      <Typography className={classes.balanceLabel}>{label}</Typography>
      <Typography variant="h5" style={{ color, fontWeight: 700 }}>
        {currencyFormat.format(value)}
      </Typography>
    </Grid>
  );

  return (
    <Paper
      className={classes.paper}
      style={
        summary.isOverpaid ? { border: `2px solid ${UNPAID_COLOR}` } : undefined
      }
    >
      <SectionTitle
        spread
        title="Payment Status"
        adornment={
          <Chip
            size="small"
            label={look.label}
            style={{
              backgroundColor: look.color,
              color: summary.isOverpaid ? "#fff" : "#000",
              fontWeight: 600,
            }}
          />
        }
        action={
          <ActionButton icon={<AddIcon />} onClick={onAddCharge}>
            Add Charge
          </ActionButton>
        }
      />

      <Box mt={2}>
        <Grid container spacing={2}>
          {tile("Order Total", summary.totalAfterDiscount)}
          {tile(
            "Paid",
            summary.netAmountPaid,
            summary.isOverpaid ? undefined : PAID_COLOR
          )}
          {summary.isOverpaid
            ? tile("Refund Due", summary.overpaid, UNPAID_COLOR)
            : tile(
                "Owed",
                summary.amountRemaining,
                summary.isPaid ? undefined : look.color
              )}
        </Grid>
      </Box>

      <LinearProgress
        variant="determinate"
        value={paidPercent}
        className={classes.progress}
        classes={{ bar: classes.progressBar }}
        style={{ "--bar-color": look.color }}
      />

      {!summary.isPaid && charge_remaining_by && (
        <Box mt={2}>
          <MuiAlert severity="warning" variant="outlined">
            Charge remaining balance by{" "}
            <strong>{moment(charge_remaining_by).format("ll")}</strong>
          </MuiAlert>
        </Box>
      )}
      {summary.isOverpaid && (
        <Box mt={2}>
          <MuiAlert severity="error" variant="filled">
            <AlertTitle>
              Refund {currencyFormat.format(summary.overpaid)} to the customer
            </AlertTitle>
            They have paid {currencyFormat.format(summary.netAmountPaid)} on an
            order total of {currencyFormat.format(summary.totalAfterDiscount)}.
            Open the charge in Transactions below to issue the refund.
          </MuiAlert>
        </Box>
      )}
    </Paper>
  );
};

// Full totals and the per-category breakdown, next to the transactions
const PaymentPanel = ({ amounts, type, onAddCharge }) => {
  const classes = useStyles();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const {
    isPackage,
    total,
    discount,
    totalAfterDiscount,
    totalPaid,
    totalRefunds,
    netAmountPaid,
    amountRemaining,
    overpaid,
    isOverpaid,
  } = getPaymentSummary(amounts, type);

  return (
    <Paper className={classes.paper}>
      <SectionTitle
        spread
        title="Payment Details"
        action={
          <ActionButton icon={<AddIcon />} onClick={onAddCharge}>
            Add Charge
          </ActionButton>
        }
      />

      <StayRow label={isPackage ? "Total" : "Total Due"}>
        <Money value={total} />
      </StayRow>
      {discount > 0 && (
        <>
          <StayRow label="Discount">
            <Money value={-discount} />
          </StayRow>
          <StayRow label="Total After Discount">
            <Money value={totalAfterDiscount} bold />
          </StayRow>
        </>
      )}
      <Divider />
      <StayRow label="Total Paid">
        <Money value={totalPaid} />
      </StayRow>
      {totalRefunds > 0 && (
        <StayRow label="Total Refunded">
          <Money value={-totalRefunds} />
        </StayRow>
      )}
      <StayRow label="Net Paid">
        <Money value={netAmountPaid} bold />
      </StayRow>
      {isOverpaid ? (
        <StayRow label="Refund Due">
          <Money value={overpaid} color={UNPAID_COLOR} />
        </StayRow>
      ) : (
        <StayRow label="Amount Remaining">
          <Money value={amountRemaining} bold />
        </StayRow>
      )}

      <Box mt={1}>
        <ActionButton
          tone="neutral"
          onClick={() => setShowBreakdown((current) => !current)}
          endIcon={showBreakdown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showBreakdown ? "Hide breakdown" : "Show breakdown"}
        </ActionButton>
      </Box>
      <Collapse in={showBreakdown}>
        {breakdownGroups(type, amounts).map((group) => {
          const rows = group.rows.filter(([, value]) => toNumber(value) !== 0);
          if (rows.length === 0) return null;

          return (
            <div key={group.label}>
              <Typography className={classes.groupLabel}>
                {group.label}
              </Typography>
              {rows.map(([label, value]) => (
                <StayRow key={label} label={label}>
                  <Money value={toNumber(value)} />
                </StayRow>
              ))}
            </div>
          );
        })}
      </Collapse>
    </Paper>
  );
};

export default PaymentPanel;
